import { useState } from 'react';
import type { Client, ClientInput } from '../../lib/clientsApi';
import { lookupAnafByCui } from '../../lib/anafApi';
import { ApiError } from '../../lib/api';
import { useCompanies } from '../../hooks/useCompanies';
import { useActiveCompanyId } from '../../store/useCompanyStore';
import { useClientIndustries, useCreateClientIndustry } from '../../hooks/useActors';
import { Select } from '../ui/Select';

// Create/edit modal for a client. Reused by the list page (create) and the
// detail header (edit). Keeps a flat form mirroring the backend zod schema.

interface ClientFormModalProps {
  title: string;
  initial?: Client;
  /** Pre-fill values for the quick-add flow (statement counterparty name). */
  prefill?: Partial<ClientInput>;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: ClientInput) => void | Promise<void>;
}

const FIELDS: { key: keyof ClientInput; label: string; type?: string; full?: boolean }[] = [
  { key: 'name', label: 'Nume *', full: true },
  { key: 'shortName', label: 'Nume scurt / nickname' },
  { key: 'cifCui', label: 'CIF / Cod TVA' },
  { key: 'regCom', label: 'Reg. Com.' },
  { key: 'country', label: 'Țară' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Telefon' },
  { key: 'website', label: 'Website' },
  { key: 'address', label: 'Adresă', full: true },
  { key: 'billingAddress', label: 'Adresă de facturare', full: true },
  { key: 'contactPerson', label: 'Persoană de contact' },
  { key: 'contactRole', label: 'Rol contact' },
  { key: 'contactEmail', label: 'Email contact', type: 'email' },
  { key: 'contactPhone', label: 'Telefon contact' },
];

export function ClientFormModal({
  title,
  initial,
  prefill,
  submitting,
  onClose,
  onSubmit,
}: ClientFormModalProps) {
  const [form, setForm] = useState<ClientInput>(() => ({
    name: initial?.name ?? prefill?.name ?? '',
    shortName: initial?.shortName ?? prefill?.shortName ?? '',
    cifCui: initial?.cifCui ?? prefill?.cifCui ?? '',
    regCom: initial?.regCom ?? prefill?.regCom ?? '',
    email: initial?.email ?? prefill?.email ?? '',
    phone: initial?.phone ?? prefill?.phone ?? '',
    website: initial?.website ?? prefill?.website ?? '',
    address: initial?.address ?? prefill?.address ?? '',
    billingAddress: initial?.billingAddress ?? prefill?.billingAddress ?? '',
    country: initial?.country ?? prefill?.country ?? 'Romania',
    contactPerson: initial?.contactPerson ?? prefill?.contactPerson ?? '',
    contactRole: initial?.contactRole ?? prefill?.contactRole ?? '',
    contactEmail: initial?.contactEmail ?? prefill?.contactEmail ?? '',
    contactPhone: initial?.contactPhone ?? prefill?.contactPhone ?? '',
    notes: initial?.notes ?? prefill?.notes ?? '',
    isActive: initial?.isActive ?? true,
  }));
  const [error, setError] = useState<string | null>(null);
  // Content-calendar colour: '' = automatic (picked from the palette).
  const [color, setColor] = useState<string>(initial?.color ?? '');

  // Multi-company: firm tag ('' = default firm). Hidden on single-firm
  // installs; new clients default to the firm from the global switcher.
  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;
  const activeCompanyId = useActiveCompanyId();
  const [companyId, setCompanyId] = useState<string>(
    initial ? (initial.companyId ?? '') : (activeCompanyId ?? ''),
  );

  // Industria ('' = not set): a dropdown with "add", shared by every firm.
  const { data: industries, isLoading: industriesLoading } = useClientIndustries();
  const createIndustry = useCreateClientIndustry();
  const [industryId, setIndustryId] = useState<string>(
    initial?.industryId ?? prefill?.industryId ?? '',
  );

  // ANAF CUI autocomplete (CLE-122) — only offered when creating a new client.
  const isCreate = !initial;
  const [cuiInput, setCuiInput] = useState('');
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafError, setAnafError] = useState<string | null>(null);
  const [anafWarning, setAnafWarning] = useState<string | null>(null);
  const [anafSuccess, setAnafSuccess] = useState<string | null>(null);

  function set(key: keyof ClientInput, value: string | boolean) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleAnafLookup() {
    const cui = cuiInput.trim();
    setAnafError(null);
    setAnafWarning(null);
    setAnafSuccess(null);
    // Client-side guard: CUI is digits (optionally RO-prefixed), 2–10 long.
    const digits = cui.replace(/^ro\s*/i, '').replace(/\s+/g, '');
    if (!/^\d{2,10}$/.test(digits)) {
      setAnafError('Cod TVA invalid. Introdu doar cifre (ex. 47847495).');
      return;
    }
    setAnafLoading(true);
    try {
      const result = await lookupAnafByCui(cui);
      if (!result.found || !result.prefill) {
        setAnafError('Codul TVA nu a fost găsit în ANAF. Completează manual.');
        return;
      }
      const p = result.prefill;
      // Pre-fill the form; the user can still edit every field before saving.
      setForm((prev) => ({
        ...prev,
        name: p.name || prev.name,
        cifCui: p.cifCui || prev.cifCui,
        regCom: p.regCom ?? prev.regCom,
        address: p.address || prev.address,
        phone: p.phone ?? prev.phone,
        notes: p.notes ?? prev.notes,
        isActive: p.isActive,
      }));
      if (result.warning) setAnafWarning(result.warning);
      else setAnafSuccess('Date preluate din ANAF. Verifică și salvează.');
    } catch (err) {
      if (err instanceof ApiError) setAnafError(err.message);
      else setAnafError('Serviciul ANAF nu răspunde. Completează manual.');
    } finally {
      setAnafLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Numele este obligatoriu.');
      return;
    }
    // Strip empty strings so optional fields stay null server-side.
    const cleaned: Record<string, unknown> = { name: form.name.trim() };
    for (const [k, v] of Object.entries(form)) {
      if (k === 'name') continue;
      if (typeof v === 'boolean') {
        cleaned[k] = v;
      } else if (typeof v === 'string' && v.trim()) {
        cleaned[k] = v.trim();
      }
    }
    if (color) cleaned.color = color;
    else if (initial?.color) cleaned.color = null;
    // Firm tag: on edit, '' explicitly clears back to the default firm (null);
    // on create, only send it when a firm was actually chosen.
    if (multiFirm) {
      if (initial) cleaned.companyId = companyId === '' ? null : companyId;
      else if (companyId) cleaned.companyId = companyId;
    }
    // Industry: on edit '' clears it; on create only a chosen one is sent.
    if (initial) cleaned.industryId = industryId === '' ? null : industryId;
    else if (industryId) cleaned.industryId = industryId;
    try {
      await onSubmit(cleaned as unknown as ClientInput);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A apărut o eroare.');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Închide"
            >
              ✕
            </button>
          </div>

          {isCreate && (
            <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Cod TVA — completare automată din ANAF
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cuiInput}
                  onChange={(e) => setCuiInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAnafLookup();
                    }
                  }}
                  placeholder="ex. 47847495 sau RO47847495"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={() => void handleAnafLookup()}
                  disabled={anafLoading}
                  className="inline-flex shrink-0 items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {anafLoading && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  Caută
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Introdu codul TVA și apasă Enter sau „Caută" pentru a precompleta datele.
              </p>
              {anafError && <p className="mt-2 text-sm text-red-600">{anafError}</p>}
              {anafWarning && (
                <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-sm text-amber-700">
                  {anafWarning}
                </p>
              )}
              {anafSuccess && <p className="mt-2 text-sm text-green-600">{anafSuccess}</p>}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                <label className="mb-1 block text-xs font-medium text-gray-600">{f.label}</label>
                <input
                  type={f.type ?? 'text'}
                  value={(form[f.key] as string) ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            ))}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Culoare în calendar
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color || '#2563eb'}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-gray-300 bg-white p-1"
                  aria-label="Culoare în calendar"
                />
                {color ? (
                  <button
                    type="button"
                    onClick={() => setColor('')}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Automată
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">Automată</span>
                )}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Industrie</label>
              <Select
                label="Industrie"
                hideLabel
                clearable
                searchable
                value={industryId}
                onChange={setIndustryId}
                loading={industriesLoading}
                placeholder="Alege sau adaugă (ex. Dental)"
                options={(industries ?? []).map((i) => ({ value: i.id, label: i.name }))}
                onCreate={async (name) => (await createIndustry.mutateAsync(name)).id}
              />
            </div>
            {multiFirm && companies && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Firmă</label>
                <Select
                  label="Firmă"
                  hideLabel
                  clearable
                  value={companyId}
                  onChange={setCompanyId}
                  placeholder="Firma implicită"
                  options={companies.map((c) => ({
                    value: c.id,
                    label: c.displayName || c.legalName,
                  }))}
                />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">Notițe</label>
              <textarea
                value={form.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
                rows={3}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={form.isActive ?? true}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              Client activ
            </label>
          </div>

          {error && <p className="px-5 pb-2 text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              Salvează
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
